import apiClient from "../../services/apiClient";
import { useEffect, useState, useContext, useMemo } from "react";
import {
  Input,
  Select,
  Button,
  Card,
  Table,
  Modal,
  Tag,
  Tooltip,
  Pagination,
  Spin,
  Alert,
  Form,
} from "antd";
import { ThemeContext } from "../../context/ThemeContext";
import {
  RiseOutlined,
  DollarOutlined,
  FileTextOutlined,
  SearchOutlined,
  EyeOutlined,
  ReloadOutlined,
  DownloadOutlined,
  TrophyOutlined,
  AppstoreOutlined,
  TableOutlined,
  ArrowLeftOutlined,
  EditOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import "./NSDL.css";
import { useNavigate } from "react-router-dom";
import { Popconfirm, message } from "antd";

const { Search } = Input;
const { Option } = Select;

// Utility function to break camelCase keys into readable format
const camelCaseToReadable = (str) => {
  return str
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/([A-Z])([A-Z][a-z])/g, "$1 $2")
    .replace(/^./, (match) => match.toUpperCase());
};

const CCILInventory = () => {
  const baseUrl = process.env.REACT_APP_BASE_URL;
  const [commercialPapers, setCommercialPapers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchText, setSearchText] = useState("");
  const [selectedCompany, setSelectedCompany] = useState("all");
  const [selectedRating, setSelectedRating] = useState("all");
  const [selectedStatus, setSelectedStatus] = useState("all");
  const [sortBy, setSortBy] = useState("companyName");
  const [viewMode, setViewMode] = useState("table");
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0,
    totalPages: 0,
  });
  const { isDark } = useContext(ThemeContext);
  const navigate = useNavigate();
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [editData, setEditData] = useState(null);
  const [editForm] = useState(() => ({ current: null }));

  // Fetch paginated data from backend
  const fetchCCILData = async (page = 1, pageSize = 20) => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiClient.get(
        `${baseUrl}/nsdl/commercial?page=${page - 1}&size=${pageSize}`,
        { validateStatus: () => true }
      );
      if (response.status === 204) {
        setCommercialPapers([]);
        setPagination({ current: 1, pageSize, total: 0, totalPages: 0 });
        return;
      }
      if (response.data && (response.data.content || response.data.data)) {
        const data = response.data.content ? response.data : response.data.data;
        setCommercialPapers(Array.isArray(data.content) ? data.content : []);
        setPagination({
          current: (data.pageNumber || 0) + 1,
          pageSize: data.pageSize || pageSize,
          total: data.totalElements || 0,
          totalPages: data.totalPages || 0,
        });
      } else {
        throw new Error(response.data.message || "Failed to fetch data");
      }
    } catch (error) {
      setError("Failed to load CCIL data. Please try again.");
      setCommercialPapers([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCCILData(pagination.current, pagination.pageSize);
    // eslint-disable-next-line
  }, []);

  // Filtering and sorting (client-side for current page)
  const filteredData = useMemo(() => {
    let filtered = Array.isArray(commercialPapers) ? commercialPapers : [];
    if (!Array.isArray(filtered)) return [];
    if (searchText) {
      filtered = filtered.filter((item) => {
        const searchLower = searchText.toLowerCase();
        return (
          (item.companyName &&
            item.companyName.toLowerCase().includes(searchLower)) ||
          (item.descriptionInNsdl &&
            item.descriptionInNsdl.toLowerCase().includes(searchLower)) ||
          (item.isin && item.isin.toLowerCase().includes(searchLower)) ||
          (item.creditRating &&
            item.creditRating.toLowerCase().includes(searchLower))
        );
      });
    }
    if (selectedCompany !== "all") {
      filtered = filtered.filter(
        (item) => item.companyName === selectedCompany
      );
    }
    if (selectedRating !== "all") {
      filtered = filtered.filter(
        (item) => item.creditRating === selectedRating
      );
    }
    if (selectedStatus !== "all") {
      filtered = filtered.filter(
        (item) => item.listingStatus === selectedStatus
      );
    }
    if (Array.isArray(filtered)) {
      filtered = [...filtered].sort((a, b) => {
        let aValue = a[sortBy];
        let bValue = b[sortBy];
        if (aValue === undefined) aValue = '';
        if (bValue === undefined) bValue = '';
        if (typeof aValue === "number" && typeof bValue === "number") {
          return bValue - aValue;
        }
        return String(aValue).localeCompare(String(bValue));
      });
    }
    return filtered;
  }, [
    commercialPapers,
    searchText,
    selectedCompany,
    selectedRating,
    selectedStatus,
    sortBy,
  ]);

  // Unique values for filters (from current page)
  const { companies, ratings, statuses } = useMemo(() => {
    const uniqueCompanies = [
      ...new Set(commercialPapers.map((item) => item.companyName)),
    ]
      .filter(Boolean)
      .slice(0, 50);
    const uniqueRatings = [
      ...new Set(commercialPapers.map((item) => item.creditRating)),
    ].filter(Boolean);
    const uniqueStatuses = [
      ...new Set(commercialPapers.map((item) => item.listingStatus)),
    ].filter(Boolean);
    return {
      companies: uniqueCompanies,
      ratings: uniqueRatings,
      statuses: uniqueStatuses,
    };
  }, [commercialPapers]);

  // Pagination change handler
  const handlePageChange = (page, pageSize) => {
    fetchCCILData(page, pageSize);
  };

  // Memoized stats
  const stats = useMemo(
    () => [
      {
        title: "Total Papers",
        value: commercialPapers.length.toLocaleString(),
        icon: <FileTextOutlined />,
        color: "#4ecdc4",
      },
      {
        title: "Active Issues",
        value: commercialPapers
          .filter((item) => new Date(item.maturityDate) > new Date())
          .length.toLocaleString(),
        icon: <RiseOutlined />,
        color: "#667eea",
      },
      {
        title: "Total Face Value",
        value:
          "₹" +
          (
            commercialPapers.reduce(
              (sum, item) => sum + (parseFloat(item.faceValue) || 0),
              0
            ) / 10000000
          ).toFixed(1) +
          " Cr",
        icon: <DollarOutlined />,
        color: "#ff6b6b",
      },
      {
        title: "Avg Rating",
        value:
          commercialPapers.length > 0
            ? commercialPapers
              .map((item) => item.creditRating?.split(",")[0])
              .filter(Boolean)
              .reduce((acc, rating) => {
                const ratingValue = rating.replace(/[^0-9]/g, "");
                return acc + (parseInt(ratingValue) || 0);
              }, 0) /
            commercialPapers.filter((item) => item.creditRating).length
            : 0,
        icon: <TrophyOutlined />,
        color: "#feca57",
      },
    ],
    [commercialPapers]
  );

  const handleRefresh = () => {
    fetchCCILData();
  };

  const handleExport = () => {
    console.log("Export functionality");
  };

  const showRecordDetails = (record) => {
    setSelectedRecord(record);
    setDetailModalVisible(true);
  };

  const getRatingColor = (rating) => {
    if (!rating) return "default";
    const ratingValue = rating.split(",")[0];
    if (ratingValue.includes("A1+") || ratingValue.includes("AAA"))
      return "green";
    if (ratingValue.includes("A1") || ratingValue.includes("AA")) return "blue";
    if (ratingValue.includes("A2") || ratingValue.includes("A"))
      return "orange";
    return "red";
  };

  const formatValue = (value, key) => {
    if (value === null || value === undefined || value === "nan") return "N/A";

    if (key.toLowerCase().includes("value") && typeof value === "number") {
      return `₹${(value / 10000000).toFixed(2)} Cr`;
    }

    if (
      key.toLowerCase().includes("rate") ||
      key.toLowerCase().includes("coupon")
    ) {
      return `${value}%`;
    }

    if (key.toLowerCase().includes("date")) {
      return new Date(value).toLocaleDateString();
    }

    return String(value);
  };

  // Edit handler
  const handleEdit = (record) => {
    setEditData(record);
    setEditModalVisible(true);
    if (editForm.current) {
      editForm.current.setFieldsValue(record);
    }
  };

  // Update handler
  const handleUpdate = async (values) => {
    setEditLoading(true);
    try {
      await apiClient.put(`${baseUrl}/nsdl/commercial/${encodeURIComponent(editData.isin)}`, values);
      message.success("Commercial Paper updated successfully.");
      setEditModalVisible(false);
      fetchCCILData(pagination.current, pagination.pageSize);
    } catch (err) {
      message.error("Failed to update Commercial Paper.");
    } finally {
      setEditLoading(false);
    }
  };

  // Delete handler
  const handleDelete = async (record) => {
    try {
      await apiClient.delete(`${baseUrl}/nsdl/commercial/${encodeURIComponent(record.isin)}`);
      message.success("Commercial Paper deleted successfully.");
      fetchCCILData(pagination.current, pagination.pageSize);
    } catch (err) {
      message.error("Failed to delete Commercial Paper.");
    }
  };

  const tableColumns = [
    {
      title: "ISIN",
      dataIndex: "isin",
      key: "isin",
      render: (isin) => <span className="isin-code">{isin}</span>,
      width: 120,
    },
    {
      title: "Company",
      dataIndex: "companyName",
      key: "companyName",
      render: (name) => (
        <Tooltip title={name}>
          <span className="company-name">
            {name.length > 30 ? `${name.substring(0, 30)}...` : name}
          </span>
        </Tooltip>
      ),
      width: 200,
    },
    {
      title: "Description",
      dataIndex: "descriptionInNsdl",
      key: "descriptionInNsdl",
      render: (desc) => (
        <Tooltip title={desc}>
          <span className="description">
            {desc.length > 40 ? `${desc.substring(0, 40)}...` : desc}
          </span>
        </Tooltip>
      ),
      width: 250,
    },
    {
      title: "Face Value",
      dataIndex: "faceValue",
      key: "faceValue",
      render: (value) => (
        <span className="face-value">
          ₹{(parseFloat(value) / 10000000).toFixed(2)} Cr
        </span>
      ),
      width: 120,
    },
    {
      title: "Issue Date",
      dataIndex: "issueDate",
      key: "issueDate",
      render: (date) => (
        <span className="date">{new Date(date).toLocaleDateString()}</span>
      ),
      width: 100,
    },
    {
      title: "Maturity Date",
      dataIndex: "maturityDate",
      key: "maturityDate",
      render: (date) => (
        <span className="date">{new Date(date).toLocaleDateString()}</span>
      ),
      width: 100,
    },
    {
      title: "Rating",
      dataIndex: "creditRating",
      key: "creditRating",
      render: (rating) => (
        <Tag color={getRatingColor(rating)} className="rating-tag">
          {rating?.split(",")[0] || "N/A"}
        </Tag>
      ),
      width: 100,
    },
    {
      title: "Status",
      dataIndex: "listingStatus",
      key: "listingStatus",
      render: (status) => (
        <Tag
          color={status?.includes("LISTED") ? "green" : "orange"}
          className="status-tag"
        >
          {status?.includes("LISTED") ? "Listed" : "Unlisted"}
        </Tag>
      ),
      width: 100,
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <span style={{ display: "flex", gap: 8 }}>
          <Tooltip title="View">
            <Button type="link" icon={<EyeOutlined />} onClick={() => showRecordDetails(record)} />
          </Tooltip>
          <Tooltip title="Edit">
            <Button type="link" icon={<EditOutlined />} onClick={() => handleEdit(record)} />
          </Tooltip>
          <Tooltip title="Delete">
            <Popconfirm
              title="Are you sure you want to delete this Commercial Paper?"
              onConfirm={() => handleDelete(record)}
              okText="Yes"
              cancelText="No"
            >
              <Button type="link" danger icon={<DeleteOutlined />} />
            </Popconfirm>
          </Tooltip>
        </span>
      ),
    },
  ];

  if (loading) {
    return (
      <div className={`nsdl-container ${isDark ? "dark" : ""}`}>
        <div className="loading-container">
          <Spin size="large" />
          <p>Loading CCIL Commercial Papers data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`nsdl-container ${isDark ? "dark" : ""}`}>
        <Alert
          message="Error"
          description={error}
          type="error"
          showIcon
          action={
            <Button size="small" danger onClick={handleRefresh}>
              Retry
            </Button>
          }
        />
      </div>
    );
  }

  return (
    <div className={`nsdl-container-cm-paper ${isDark ? "dark" : ""}`}>
      <Button
        type="default"
        icon={<ArrowLeftOutlined />}
        onClick={() => navigate("/d/nsdl")}
        style={{ marginBottom: 16 }}
      >
        Back to NSDL
      </Button>
      {/* Hero Section */}
      <div
        className="nsdl-hero"
        style={{
          background: 'linear-gradient(90deg, #3AA015 0%, #F49A24 50%, rgba(255, 0, 4, 0.61) 100%)',
          borderRadius: 18,
          padding: '2px',
          marginBottom: 32,
          position: 'relative',
        }}
      >
        <div
          style={{
            background: 'transparent',
            borderRadius: 16,
            padding: "48px 48px 40px 48px",
            boxShadow: "0 4px 20px rgba(0, 0, 0, 0.08)",
            position: 'relative',
            overflow: 'hidden',
            minHeight: 180,
            width: '100%',
            height: '100%',
          }}
        >
          {/* Overlay for better text readability */}
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: isDark 
                ? 'linear-gradient(135deg, rgba(0, 0, 0, 0.7) 0%, rgba(0, 0, 0, 0.5) 100%)'
                : 'linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(255, 255, 255, 0.7) 100%)',
              borderRadius: 16,
            }}
          />

          {/* Content */}
          <div style={{ position: 'relative', zIndex: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <h1
                style={{
                  margin: 0,
                  marginBottom: 12,
                  fontWeight: 800,
                  fontSize: 42,
                  color: isDark ? "#ffffff" : "#1a1a1a",
                }}
              >
                Commercial Papers
              </h1>
              <p
                style={{
                  fontSize: 20,
                  color: isDark ? "rgba(255, 255, 255, 0.9)" : "#4a4a4a",
                  fontWeight: 400,
                  lineHeight: 1.4,
                  margin: 0,
                }}
              >
                Comprehensive database of Commercial Papers with detailed issuer information, ratings, and market data
              </p>
            </div>

            {/* Stats on the right side - 2 per row */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', alignItems: 'flex-end' }}>
              {/* First row - 2 stats */}
              <div style={{ display: 'flex', gap: '12px' }}>
                {stats.slice(0, 2).map((stat, index) => (
                  <div key={index} style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    backgroundColor: isDark ? 'rgba(0, 0, 0, 0.6)' : 'rgba(255, 255, 255, 0.8)',
                    padding: '12px 16px',
                    borderRadius: 12,
                    boxShadow: isDark ? '0 2px 8px rgba(0, 0, 0, 0.3)' : '0 2px 8px rgba(0, 0, 0, 0.1)',
                    minWidth: '180px'
                  }}>
                    <div
                      style={{
                        color: stat.color,
                        fontSize: 20,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 36,
                        height: 36,
                        backgroundColor: isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(255, 255, 255, 0.9)',
                        borderRadius: 8,
                        boxShadow: isDark ? '0 2px 4px rgba(0, 0, 0, 0.3)' : '0 2px 4px rgba(0, 0, 0, 0.1)'
                      }}
                    >
                      {stat.icon}
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                      <span style={{
                        fontSize: 20,
                        fontWeight: 700,
                        color: isDark ? "#ffffff" : "#1a1a1a",
                        lineHeight: 1
                      }}>
                        {stat.value}
                      </span>
                      <span style={{
                        fontSize: 12,
                        color: isDark ? "rgba(255, 255, 255, 0.8)" : "#666",
                        fontWeight: 500
                      }}>
                        {stat.title}
                      </span>
                    </div>
                  </div>
                ))}
              </div>

              {/* Second row - remaining stats */}
              {stats.length > 2 && (
                <div style={{ display: 'flex', gap: '12px' }}>
                  {stats.slice(2).map((stat, index) => (
                    <div key={index + 2} style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      backgroundColor: isDark ? 'rgba(0, 0, 0, 0.6)' : 'rgba(255, 255, 255, 0.8)',
                      padding: '12px 16px',
                      borderRadius: 12,
                      boxShadow: isDark ? '0 2px 8px rgba(0, 0, 0, 0.3)' : '0 2px 8px rgba(0, 0, 0, 0.1)',
                      minWidth: '180px'
                    }}>
                      <div
                        style={{
                          color: stat.color,
                          fontSize: 20,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: 36,
                          height: 36,
                          backgroundColor: isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(255, 255, 255, 0.9)',
                          borderRadius: 8,
                          boxShadow: isDark ? '0 2px 4px rgba(0, 0, 0, 0.3)' : '0 2px 4px rgba(0, 0, 0, 0.1)'
                        }}
                      >
                        {stat.icon}
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                        <span style={{
                          fontSize: 20,
                          fontWeight: 700,
                          color: isDark ? "#ffffff" : "#1a1a1a",
                          lineHeight: 1
                        }}>
                          {stat.value}
                        </span>
                        <span style={{
                          fontSize: 12,
                          color: isDark ? "rgba(255, 255, 255, 0.8)" : "#666",
                          fontWeight: 500
                        }}>
                          {stat.title}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Data Section */}
      <div className="nsdl-section">

        {/* Controls */}
        <div className="nsdl-controls">
          <div className="nsdl-controls-left">
            <Search
              placeholder="Search by company, instrument name, or ISIN..."
              allowClear
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              style={{ width: 300 }}
              prefix={<SearchOutlined />}
            />

            <Select
              value={selectedCompany}
              onChange={setSelectedCompany}
              placeholder="Filter by Company"
              style={{ width: 200 }}
              allowClear
            >
              <Option value="all">All Companies</Option>
              {companies.map((company) => (
                <Option key={company} value={company}>
                  {company}
                </Option>
              ))}
            </Select>

            <Select
              value={selectedRating}
              onChange={setSelectedRating}
              placeholder="Filter by Rating"
              style={{ width: 150 }}
              allowClear
            >
              <Option value="all">All Ratings</Option>
              {ratings.map((rating) => (
                <Option key={rating} value={rating}>
                  {rating.split(",")[0]}
                </Option>
              ))}
            </Select>

            <Select
              value={selectedStatus}
              onChange={setSelectedStatus}
              placeholder="Filter by Status"
              style={{ width: 150 }}
              allowClear
            >
              <Option value="all">All Status</Option>
              {statuses.map((status) => (
                <Option key={status} value={status}>
                  {status.includes("LISTED") ? "Listed" : "Unlisted"}
                </Option>
              ))}
            </Select>
          </div>

          <div className="nsdl-controls-right">
            <Button.Group>
              <Button
                type={viewMode === "cards" ? "primary" : "default"}
                icon={<AppstoreOutlined />}
                onClick={() => setViewMode("cards")}
              >
                Cards
              </Button>
              <Button
                type={viewMode === "table" ? "primary" : "default"}
                icon={<TableOutlined />}
                onClick={() => setViewMode("table")}
              >
                Table
              </Button>
            </Button.Group>

            <Button
              type="primary"
              icon={<ReloadOutlined />}
              onClick={handleRefresh}
              loading={loading}
            >
              Refresh
            </Button>

            <Button icon={<DownloadOutlined />} onClick={handleExport}>
              Export
            </Button>
          </div>
        </div>

        {/* Results Info */}
        <div className="nsdl-results-info">
          <span>
            Showing {filteredData.length} of {pagination.total} papers (Page {pagination.current} of {pagination.totalPages})
          </span>
        </div>
      </div>

      {/* Results Section */}
      {viewMode === "cards" ? (
        <div className="cards-grid">
          {filteredData.map((paper, index) => (
            <Card key={index} className="paper-card" hoverable>
              <div className="card-header">
                <div className="isin-badge">{paper.isin}</div>
                <Tag
                  color={getRatingColor(paper.creditRating)}
                  className="rating-badge"
                >
                  {paper.creditRating?.split(",")[0] || "N/A"}
                </Tag>
              </div>

              <div className="card-content">
                <h3 className="company-name">
                  <Tooltip title={paper.companyName}>
                    {paper.companyName.length > 40
                      ? `${paper.companyName.substring(0, 40)}...`
                      : paper.companyName}
                  </Tooltip>
                </h3>

                <p className="description">
                  <Tooltip title={paper.descriptionInNsdl}>
                    {paper.descriptionInNsdl.length > 60
                      ? `${paper.descriptionInNsdl.substring(0, 60)}...`
                      : paper.descriptionInNsdl}
                  </Tooltip>
                </p>

                <div className="card-details">
                  <div className="detail-item">
                    <span className="label">Face Value:</span>
                    <span className="value">
                      ₹{(parseFloat(paper.faceValue) / 10000000).toFixed(2)}{" "}
                      Cr
                    </span>
                  </div>

                  <div className="detail-item">
                    <span className="label">Issue Date:</span>
                    <span className="value">
                      {new Date(paper.issueDate).toLocaleDateString()}
                    </span>
                  </div>

                  <div className="detail-item">
                    <span className="label">Maturity:</span>
                    <span className="value">
                      {new Date(paper.maturityDate).toLocaleDateString()}
                    </span>
                  </div>

                  <div className="detail-item">
                    <span className="label">Status:</span>
                    <Tag
                      color={
                        paper.listingStatus?.includes("LISTED")
                          ? "green"
                          : "orange"
                      }
                    >
                      {paper.listingStatus?.includes("LISTED")
                        ? "Listed"
                        : "Unlisted"}
                    </Tag>
                  </div>
                </div>
              </div>

              <div className="card-actions" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 12, marginTop: 16 }}>
                <Tooltip title="View">
                  <Button
                    type="primary"
                    icon={<EyeOutlined />}
                    onClick={() => showRecordDetails(paper)}
                    className="view-btn"
                  />
                </Tooltip>
                <Tooltip title="Edit">
                  <Button
                    icon={<EditOutlined />}
                    onClick={() => handleEdit(paper)}
                  />
                </Tooltip>
                <Tooltip title="Delete">
                  <Popconfirm
                    title="Are you sure you want to delete this Commercial Paper?"
                    onConfirm={() => handleDelete(paper)}
                    okText="Yes"
                    cancelText="No"
                  >
                    <Button danger icon={<DeleteOutlined />} />
                  </Popconfirm>
                </Tooltip>
              </div>
            </Card>
          ))}
        </div>
      ) : (
        <div className="table-container">
          <Table
            columns={tableColumns}
            dataSource={filteredData}
            rowKey="isin"
            pagination={false}
            size="middle"
            className={`nsdl-table ${isDark ? "dark" : ""}`}
          />
        </div>
      )}

      {/* Pagination */}
      <div className="pagination-section">
        <Pagination
          current={pagination.current}
          total={pagination.total}
          pageSize={pagination.pageSize}
          showSizeChanger
          showQuickJumper
          showTotal={(total, range) =>
            `${range[0]}-${range[1]} of ${total} items`
          }
          onChange={handlePageChange}
          className="custom-pagination"
        />
      </div>

      {/* Detail Modal */}
      <Modal
        title="Commercial Paper Details"
        open={detailModalVisible}
        onCancel={() => setDetailModalVisible(false)}
        footer={null}
        width={800}
        className={`detail-modal ${isDark ? "dark" : ""}`}
        bodyStyle={{
          backgroundColor: isDark ? "#2a2a3e" : "#ffffff",
          color: isDark ? "#e0e0e0" : "#333333",
          maxHeight: "60vh",
          overflowY: "auto",
        }}
      >
        {selectedRecord && (
          <div className="modal-content">
            <div className="modal-header">
              <div className="modal-isin">{selectedRecord.isin}</div>
              <Tag
                color={getRatingColor(selectedRecord.creditRating)}
                className="modal-rating"
              >
                {selectedRecord.creditRating?.split(",")[0] || "N/A"}
              </Tag>
            </div>

            <div className="modal-sections">
              <div className="modal-section">
                <h4>Basic Information</h4>
                <div className="info-grid">
                  <div className="info-item">
                    <span className="label">Company:</span>
                    <span className="value">{selectedRecord.companyName}</span>
                  </div>
                  <div className="info-item">
                    <span className="label">Description:</span>
                    <span className="value">
                      {selectedRecord.descriptionInNsdl}
                    </span>
                  </div>
                  <div className="info-item">
                    <span className="label">Face Value:</span>
                    <span className="value">
                      ₹
                      {(
                        parseFloat(selectedRecord.faceValue) / 10000000
                      ).toFixed(2)}{" "}
                      Cr
                    </span>
                  </div>
                  <div className="info-item">
                    <span className="label">Issue Value:</span>
                    <span className="value">
                      {selectedRecord.issueValue
                        ? `₹${(
                          parseFloat(selectedRecord.issueValue) / 10000000
                        ).toFixed(2)} Cr`
                        : "N/A"}
                    </span>
                  </div>
                </div>
              </div>

              <div className="modal-section">
                <h4>Dates & Status</h4>
                <div className="info-grid">
                  <div className="info-item">
                    <span className="label">Issue Date:</span>
                    <span className="value">
                      {new Date(selectedRecord.issueDate).toLocaleDateString()}
                    </span>
                  </div>
                  <div className="info-item">
                    <span className="label">Maturity Date:</span>
                    <span className="value">
                      {new Date(
                        selectedRecord.maturityDate
                      ).toLocaleDateString()}
                    </span>
                  </div>
                  <div className="info-item">
                    <span className="label">Listing Status:</span>
                    <span className="value">
                      <Tag
                        color={
                          selectedRecord.listingStatus?.includes("LISTED")
                            ? "green"
                            : "orange"
                        }
                      >
                        {selectedRecord.listingStatus}
                      </Tag>
                    </span>
                  </div>
                </div>
              </div>

              <div className="modal-section">
                <h4>Rating Information</h4>
                <div className="info-grid">
                  <div className="info-item">
                    <span className="label">Credit Rating:</span>
                    <span className="value">
                      <Tag color={getRatingColor(selectedRecord.creditRating)}>
                        {selectedRecord.creditRating}
                      </Tag>
                    </span>
                  </div>
                  <div className="info-item">
                    <span className="label">Rating Agency:</span>
                    <span className="value">
                      {selectedRecord.creditRatingAgency}
                    </span>
                  </div>
                </div>
              </div>

              <div className="modal-section">
                <h4>Contact Information</h4>
                <div className="info-grid">
                  <div className="info-item">
                    <span className="label">RTA Agent:</span>
                    <span className="value">{selectedRecord.rtaAgentBpId}</span>
                  </div>
                  <div className="info-item">
                    <span className="label">Issuing Agent:</span>
                    <span className="value">
                      {selectedRecord.issuingPayingAgent}
                    </span>
                  </div>
                  <div className="info-item">
                    <span className="label">Company Address:</span>
                    <span className="value">
                      {selectedRecord.companyAddress}
                    </span>
                  </div>
                  <div className="info-item">
                    <span className="label">IPA Demat Details:</span>
                    <span className="value">
                      {selectedRecord.ipaDematDetails}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </Modal>

      {/* Edit Modal */}
      <Modal
        title={editData ? `Edit Commercial Paper - ${editData.isin}` : "Edit Commercial Paper"}
        open={editModalVisible}
        onCancel={() => setEditModalVisible(false)}
        footer={null}
        width={800}
        destroyOnClose
      >
        {editData && (
          <Form
            initialValues={editData}
            onFinish={handleUpdate}
            layout="vertical"
            ref={editForm}
          >
            <Form.Item noStyle>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 0 }}>
                {(() => {
                  const fields = Object.entries(editData).filter(([key]) => key !== 'createdAt' && key !== 'updatedAt');
                  const items = [];
                  for (let i = 0; i < fields.length; i += 2) {
                    const [key1, value1] = fields[i];
                    const [key2, value2] = fields[i + 1] || [];
                    items.push(
                      <div key={key1 + (key2 || '')} style={{ display: 'flex', width: '100%', gap: 16 }}>
                        <Form.Item
                          key={key1}
                          label={camelCaseToReadable(key1)}
                          name={key1}
                          rules={key1 === "isin" ? [] : [{ required: false }]}
                          style={{ flex: 1 }}
                        >
                          {key1 === "isin" ? <Input disabled /> : <Input />}
                        </Form.Item>
                        {key2 && (
                          <Form.Item
                            key={key2}
                            label={camelCaseToReadable(key2)}
                            name={key2}
                            rules={key2 === "isin" ? [] : [{ required: false }]}
                            style={{ flex: 1 }}
                          >
                            {key2 === "isin" ? <Input disabled /> : <Input />}
                          </Form.Item>
                        )}
                      </div>
                    );
                  }
                  return items;
                })()}
              </div>
            </Form.Item>
            <div style={{ textAlign: "right" }}>
              <Button onClick={() => setEditModalVisible(false)} style={{ marginRight: 8 }}>
                Cancel
              </Button>
              <Button type="primary" htmlType="submit" loading={editLoading}>
                Update
              </Button>
            </div>
          </Form>
        )}
      </Modal>
    </div>
  );
};

export default CCILInventory;
